import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { useAuth } from '@/shared/contexts/AuthContext';
import { isEmailVerified } from '@/shared/utils/emailVerified';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import { coordinatedInstitution, isAdmin } from '@/shared/utils/roles';
import './NavBar.css';

function NavBar() {
    const { currentUser, userProfile, logout } = useAuth();
    const navigate = useNavigate();

    // Sign-out navigates home whether or not logout() resolved, so a failure
    // never strands the user on an authenticated view. The catch is load
    // bearing: React does not await a click handler, so reducing this to a
    // bare `finally` turns a failed sign-out into an unhandled rejection.
    const handleLogout = async () => {
        try {
            await logout();
        } catch (_error) {
            // No recovery is possible; the navigation below is the response.
        } finally {
            navigate('/');
        }
    };

    const signedIn = currentUser && isEmailVerified(currentUser);
    // A coordinator reaches the same admin area, scoped to their own
    // institution, so the same pill says which of the two they hold.
    const areaLabel = isAdmin(userProfile)
        ? 'Admin'
        : coordinatedInstitution(userProfile)
          ? 'Coordinator'
          : null;

    // Expanded from xl, not lg. A signed-in admin's bar carries five links,
    // the Admin pill, a name, and an institution logo up to CU Denver's width,
    // which needs the full 1140px xl container. From 992px to 1199px the lg
    // container clipped the user menu off the right edge, and 1024px is what
    // a 1280px laptop shows at 125% zoom.
    return (
        <Navbar expand="xl" className="afe-navbar" variant="dark">
            <Container>
                <Navbar.Brand as={Link} to="/" className="afe-brand">
                    Auraria Furniture Exchange
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="main-navbar" />
                <Navbar.Collapse id="main-navbar">
                    <Nav className="me-auto">
                        <Nav.Link as={NavLink} to="/" end>
                            Home
                        </Nav.Link>
                        {signedIn && (
                            <>
                                <Nav.Link as={NavLink} to="/listings" end>
                                    Browse
                                </Nav.Link>
                                <Nav.Link as={NavLink} to="/listings/new">
                                    Post an item
                                </Nav.Link>
                                <Nav.Link as={NavLink} to="/listings/mine">
                                    My listings
                                </Nav.Link>
                                <Nav.Link as={NavLink} to="/requests/mine">
                                    My requests
                                </Nav.Link>
                            </>
                        )}
                    </Nav>
                    <Nav className="align-items-xl-center">
                        {/*
                          The admin area is reached from the bar itself rather
                          than from inside the name menu, where an admin had no
                          way to tell it existed. It sits outside the member
                          links on the left because it is a different kind of
                          place, not another thing members do.
                        */}
                        {signedIn && areaLabel && (
                            <Nav.Link
                                as={NavLink}
                                to="/admin"
                                className="afe-nav-admin me-xl-2"
                            >
                                {areaLabel}
                            </Nav.Link>
                        )}
                        {signedIn ? (
                            <NavDropdown
                                align="end"
                                title={
                                    <span>
                                        {userProfile?.name || currentUser.email}{' '}
                                        {userProfile && (
                                            <InstitutionLogo
                                                institution={
                                                    userProfile.institution
                                                }
                                                surface="dark"
                                                className="ms-1"
                                            />
                                        )}
                                    </span>
                                }
                                id="user-menu"
                            >
                                <NavDropdown.Item as={Link} to="/profile">
                                    Profile
                                </NavDropdown.Item>
                                <NavDropdown.Divider />
                                <NavDropdown.Item onClick={handleLogout}>
                                    Log out
                                </NavDropdown.Item>
                            </NavDropdown>
                        ) : (
                            <>
                                <Nav.Link as={NavLink} to="/login">
                                    Log in
                                </Nav.Link>
                                <Nav.Link as={NavLink} to="/signup">
                                    Sign up
                                </Nav.Link>
                            </>
                        )}
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
}

export default NavBar;
